import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { receptionApiErrorMessage } from './receptionConstants';
import { Plus, RefreshCw } from 'lucide-react';
import MobileInput from '../ui/mobile-input';
import DateTimeInput from '../ui/datetime-input';
import { validateMobile } from '../../utils/phoneValidation';
import { validateDateTime } from '../../utils/dateValidation';
import { dateTimeLocalValue } from '../../utils/appDateTime';

const empty = () => ({
  visitor_name: '', phone: '', purpose: '', person_to_meet: '',
  check_in_time: dateTimeLocalValue(), remarks: '',
});

export default function ReceptionVisitors() {
  const { toast } = useToast();
  const { canManageFeature, hasPermission } = useAuth();
  const canManage = canManageFeature('reception') || hasPermission('manage_reception');
  const canDelete = hasPermission('delete_reception');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [phoneError, setPhoneError] = useState('');
  const [datetimeError, setDatetimeError] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reception/visitors');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const save = async () => {
    if (!form.visitor_name?.trim()) {
      toast({ title: 'Visitor name required', variant: 'destructive' });
      return;
    }
    const err = validateMobile(form.phone, { required: false });
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    const dtErr = validateDateTime(form.check_in_time, { required: false });
    if (dtErr) {
      setDatetimeError(dtErr);
      toast({ title: 'Invalid date and time', description: dtErr, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    setDatetimeError('');
    try {
      await api.post('/reception/visitors', form);
      toast({ title: 'Visitor recorded' });
      setOpen(false);
      setForm(empty());
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const checkout = async (id) => {
    try {
      await api.post(`/reception/visitors/${id}/checkout`);
      toast({ title: 'Checked out' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete visitor record?')) return;
    try {
      await api.delete(`/reception/visitors/${id}`);
      toast({ title: 'Deleted' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h1 className="text-2xl font-bold">Visitors</h1><p className="text-muted-foreground">Check-in and check-out log</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchItems}><RefreshCw className="w-4 h-4" /></Button>
          {canManage && <Button onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Visitor</Button>}
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle>Today & Recent Visitors</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Loading…</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground">
                <th className="py-2">Name</th><th className="py-2">Phone</th><th className="py-2">Purpose</th>
                <th className="py-2">Meet</th><th className="py-2">Check-in</th><th className="py-2">Check-out</th><th className="py-2">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-2 font-medium">{r.visitor_name}</td>
                    <td className="py-2">{r.phone || '—'}</td>
                    <td className="py-2">{r.purpose || '—'}</td>
                    <td className="py-2">{r.person_to_meet || '—'}</td>
                    <td className="py-2">{r.check_in_time || '—'}</td>
                    <td className="py-2">{r.check_out_time || '—'}</td>
                    <td className="py-2 flex gap-2">
                      {canManage && !r.check_out_time && <Button size="sm" variant="outline" onClick={() => checkout(r.id)}>Check out</Button>}
                      {canDelete && <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>Delete</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Register Visitor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} /></div>
            <div><Label>Phone</Label><MobileInput value={form.phone} onChange={(v) => { setForm({ ...form, phone: v }); setPhoneError(''); }} error={phoneError} /></div>
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} /></div>
            <div><Label>Person to Meet</Label><Input value={form.person_to_meet} onChange={(e) => setForm({ ...form, person_to_meet: e.target.value })} /></div>
            <div><Label>Check-in Time</Label><DateTimeInput value={form.check_in_time} onChange={(v) => { setForm({ ...form, check_in_time: v }); setDatetimeError(''); }} error={datetimeError} /></div>
            <div><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
